import {
	type CsvColumnMapping,
	type CsvPaymentDraft,
	guessCsvMapping,
	parseGenericCsv,
	readCsvTable,
} from "@sobidogam/report";
import { useMemo, useState } from "react";
import { Sheet } from "./Sheet";

/**
 * #46 다른 형식: pick which column is the date, amount and store, preview the
 * first rows, then import. Rows whose date cannot be read are listed by row
 * number instead of being saved with today's date.
 */
export function GenericCsvImport({
	text,
	onCancel,
	onImport,
}: {
	text: string;
	onCancel: () => void;
	onImport: (records: CsvPaymentDraft[], dateRejectedRows: number[]) => void;
}) {
	const table = useMemo(() => readCsvTable(text), [text]);
	const [mapping, setMapping] = useState<CsvColumnMapping>(() =>
		guessCsvMapping(table.headers),
	);
	const result = useMemo(() => parseGenericCsv(text, mapping), [text, mapping]);
	const ready = mapping.date >= 0 && mapping.amount >= 0 && mapping.store >= 0;
	const dateRows = result.rejected
		.filter((item) => item.reason === "date")
		.map((item) => item.rowNumber);
	const column = (
		key: keyof CsvColumnMapping,
		label: string,
		optional = false,
	) => (
		<label className="csv-map-row">
			<span>{label}</span>
			<select
				value={mapping[key] ?? -1}
				onChange={(event) => {
					const value = Number(event.target.value);
					setMapping({
						...mapping,
						[key]: optional && value < 0 ? null : value,
					});
				}}
			>
				<option value={-1}>{optional ? "없음" : "열 고르기"}</option>
				{table.headers.map((header, index) => (
					<option key={`${index}-${header}`} value={index}>
						{header || `${index + 1}번째 열`}
					</option>
				))}
			</select>
		</label>
	);
	return (
		<Sheet
			title="열을 골라 가져오기"
			onClose={onCancel}
			footer={
				<button
					className="button full-width"
					type="button"
					disabled={!ready || !result.records.length}
					onClick={() => onImport(result.records, dateRows)}
				>
					{ready
						? `${result.records.length}건 가져오기`
						: "열을 먼저 골라 주세요"}
				</button>
			}
		>
			{table.headers.length < 2 ? (
				<p className="form-error" role="alert">
					표를 읽지 못했어요. 엑셀에서 'CSV UTF-8'로 저장한 파일인지 확인해
					주세요.
				</p>
			) : (
				<>
					<p className="field-hint">
						{table.rows.length}행을 찾았어요. 날짜·금액·내용 열을 골라 주세요.
					</p>
					<div className="csv-map">
						{column("date", "날짜")}
						{column("amount", "금액")}
						{column("store", "내용(매장)")}
						{column("category", "분류", true)}
					</div>
					{ready ? (
						<div className="guide-table-wrap">
							<table className="guide-table">
								<caption className="sr-only">미리 보기</caption>
								<thead>
									<tr>
										<th scope="col">날짜</th>
										<th scope="col">내용</th>
										<th scope="col">금액</th>
									</tr>
								</thead>
								<tbody>
									{result.records.slice(0, 5).map((record) => (
										<tr key={record.rowNumber}>
											<td>{record.transactedAt.slice(0, 10)}</td>
											<td>{record.storeDisplay}</td>
											<td>{record.totalAmount.toLocaleString("ko-KR")}원</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : null}
					{dateRows.length ? (
						<p className="notice">
							{dateRows.length}행은 날짜를 못 읽었어요 (
							{dateRows.slice(0, 8).join(", ")}
							{dateRows.length > 8 ? " …" : ""}행).
						</p>
					) : null}
				</>
			)}
		</Sheet>
	);
}
